import Slider from "react-slick"

interface BepoFloatingPlayerTitleProps {
    title: string
    bepoHomeUrl: string
    /** When true (read-aloud, no promo image): 2-line clamp with ellipsis instead of marquee. */
    multilineClamp?: boolean
    /** When true (read-aloud): hide BePo logo under title — branding lives in sponsor footer. */
    hideBepoLogo?: boolean
}

interface BepoFloatingPlayerTitleState {
    isTitleOverFlowing: boolean
    titleWidth: string
    marqueeSpeedMs: number
}

/** Keep in sync with `.bepo-fp-title-slider .slick-slide { margin-left }` */
const SLIDE_GAP_PX = 70
/** Constant marquee velocity (px/s) — slow enough to read comfortably */
const MARQUEE_PX_PER_SEC = 25
const MARQUEE_MIN_MS = 12000

const SLIDER_SETTINGS = {
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 0,
    rtl: true,
    arrows: false,
    cssEase: "linear",
    waitForAnimate: true,
    pauseOnHover: false,
    pauseOnFocus: false,
}

function calcMarqueeSpeedMs(titleWidthPx: number, titleLength: number) {
    if (titleWidthPx > 0) {
        const distancePx = titleWidthPx + SLIDE_GAP_PX
        return Math.max((distancePx / MARQUEE_PX_PER_SEC) * 1000, MARQUEE_MIN_MS)
    }
    // Fallback before measure — slightly slower than the old title.length * 200
    return Math.max(titleLength * 250, MARQUEE_MIN_MS)
}

export class BepoFloatingPlayerTitle extends React.PureComponent<BepoFloatingPlayerTitleProps, BepoFloatingPlayerTitleState> {
    private titleMeasureRef: HTMLDivElement = null
    private wrapperRef: HTMLDivElement = null

    constructor() {
        super()
        this.state = {
            isTitleOverFlowing: null,
            titleWidth: "0px",
            marqueeSpeedMs: MARQUEE_MIN_MS,
        }
    }

    componentDidMount() {
        this.measureTitle()
    }

    componentDidUpdate(prevProps: BepoFloatingPlayerTitleProps) {
        if (prevProps.title !== this.props.title) {
            this.measureTitle()
        }
    }

    private measureTitle = () => {
        if (this.titleMeasureRef && this.wrapperRef) {
            const titleWidthPx = this.titleMeasureRef.offsetWidth
            const overflows = titleWidthPx > this.wrapperRef.offsetWidth
            this.setState({
                isTitleOverFlowing: overflows,
                titleWidth: titleWidthPx + "px",
                marqueeSpeedMs: calcMarqueeSpeedMs(titleWidthPx, this.props.title.length),
            })
        }
    }

    private renderBepoLogo = () => {
        const { bepoHomeUrl, hideBepoLogo } = this.props
        if (hideBepoLogo) {
            return null
        }
        return (
            <a
                className="bepo-fp-bepo-logo-btn"
                href={bepoHomeUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="bepo"
            />
        )
    }

    render() {
        const { title, multilineClamp } = this.props
        const { isTitleOverFlowing, titleWidth, marqueeSpeedMs } = this.state

        if (multilineClamp) {
            return (
                <div className="bepo-fp-title-section bepo-fp-title-section--multiline">
                    <div className="bepo-fp-title-wrapper bepo-fp-title-wrapper--multiline">
                        <div className="bepo-fp-title-text bepo-fp-title-text--multiline">{title}</div>
                    </div>
                </div>
            )
        }

        const sliderSettings = {
            ...SLIDER_SETTINGS,
            speed: marqueeSpeedMs,
        }

        return (
            <div className="bepo-fp-title-section">
                <div className="bepo-fp-title-wrapper" ref={(ref) => this.wrapperRef = ref}>
                    {isTitleOverFlowing ? (
                        <>
                            <div className="bepo-fp-title-slider" style={{ width: titleWidth }}>
                                <Slider key={`${title}-${marqueeSpeedMs}`} {...sliderSettings}>
                                    <div className="bepo-fp-title-text">{title}</div>
                                    <div className="bepo-fp-title-text">{title}</div>
                                </Slider>
                            </div>
                            <div className="bepo-fp-title-fader-left" />
                        </>
                    ) : (
                        <div className="bepo-fp-title-text">{title}</div>
                    )}
                    <div
                        className="bepo-fp-title-text"
                        ref={(ref) => this.titleMeasureRef = ref}
                        style={{
                            position: "absolute",
                            visibility: "hidden",
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                            height: 0,
                            overflow: "hidden",
                        }}
                        aria-hidden="true"
                    >
                        {title}
                    </div>
                </div>
                {this.renderBepoLogo()}
            </div>
        )
    }
}
